import { v4 as uuid } from 'uuid'

import {
  IRecord_Nopress_Pod,
  IRecord_Nopress_Page,
  INopress_Template_Config,
  INopress_Section_Config,
  INopress_Pod_Config,
  INopress_Form_Config,
  INopress_Page,
  INopress_Template_Structure,
  INopress_Render_Page_Context,
  IRecord_Nopress_Page_Core_Metadata_Page,
} from '../types/schema/nopress'

import {
  IData_Org_With_Profile,
} from '../types/rbac'

import {
  IMeta_ProjectConfig_App_Config_Nopress,
} from '../types/schema/project_config'

import {
  IData_Nopress_List_Data,
} from '../types/nopress'

import {
  IForm_Schema,
} from '../types/schema/form'

import {
  INode_Org,
  ITree_Org,
} from '../types/schema/tree'

import {
  findParentOrg,
  getOrgAppConfig,
} from './tree'

export const processTemplateConfig = (config: INopress_Template_Config): INopress_Template_Config => {
  const copy = JSON.parse(JSON.stringify(config)) as INopress_Template_Config

  const collectPage = (page: INopress_Page) => {
    let ret: INopress_Page = Object.assign({}, config.config.page_defaults, {
      meta: {},
    }, page, {
      pages: (page.pages || []).map(collectPage)
    })
    // assign the page layout to the collection
    // unless the collection has got it's own ideas
    if(ret.collection) {
      if(!ret.collection.layout) ret.collection.layout = ret.layout
    }
    return ret
  }

  copy.content = copy.content.map(section => {
    return Object.assign({}, section, {
      pages: section.pages.map(collectPage)
    })
  })

  return copy
}

export const podDataHelper = (data: IData_Nopress_List_Data) => data.pods.reduce<Record<string, IRecord_Nopress_Pod[]>>((all, pod) => {
  const id = `${pod.page_id}:${pod.pod_id}`
  const existing = all[id] || []
  all[id] = existing.concat([pod])
  return all
}, {})

export const pageDataHelper = (data: IData_Nopress_List_Data) => data.pages.reduce<Record<string, IRecord_Nopress_Page>>((all, page) => {
  all[page.page_id] = page
  return all
}, {})

export const pageAncestorsHelper = (config: INopress_Template_Config): Record<string, string[]> => {
  if(!config) return {}
  const ancestorsMap: Record<string, string[]> = {}
  const addPage = (ancestors: string[], page: INopress_Page) => {
    const children = page.pages || []
    ancestorsMap[page.id] = ancestors
    children.forEach(child => {
      addPage(ancestors.concat([page.id]), child)
    })
  }
  config.content.forEach(section => {
    ancestorsMap[section.id] = ['root']
    section.pages.forEach(page => {
      addPage(section.hideFolder ? ['root'] : ['root', section.id], page)
    })
  })
  return ancestorsMap
}

export const pageSectionsHelper = (config: INopress_Template_Config): Record<string, string> => {
  if(!config) return {}
  const sectionMap: Record<string, string> = {}
  const addPage = (section: string, page: INopress_Page) => {
    const children = page.pages || []
    sectionMap[page.id] = section
    children.forEach(child => {
      addPage(section, child)
    })
  }
  config.content.forEach(section => {      
    section.pages.forEach(page => {
      addPage(section.id, page)
    })
  })
  return sectionMap
}

export const structureHelper = (config: INopress_Template_Config): INopress_Template_Structure => {
  const structure: INopress_Template_Structure = {
    sections: {},
    pages: {},
    pods: {},
    forms: {}, 
  }
  if(!config) return structure
  config.content.forEach(section => {
    structure.sections[section.id] = section
    const addPage = (page: INopress_Page) => {
      const children = page.pages || []
      structure.pages[page.id] = page
      children.forEach(addPage)
    }
    section.pages.forEach(page => {
      addPage(page)
    })
  })
  config.pods.forEach(pod => {
    structure.pods[pod.id] = pod
  })
  config.forms.forEach(form => {
    structure.forms[form.id] = form
  })
  return structure
}

export const createCollectionId = () => uuid()
export const mergeCollectionId = (pageid: string, collectionid: string) => [pageid, collectionid].join(':')
export const extractPageId = (pageid: string) => pageid.split(':')[0]
export const extractCollectionId = (pageid: string) => pageid.split(':')[1] || ''

export const getPageFromId = (config: INopress_Template_Config, pageid: string) => {
  const { pages } = structureHelper(config)
  return pages[extractPageId(pageid)]
}

export const identifyPage = (config: INopress_Template_Config, pageid: string) => {
  const page = getPageFromId(config, pageid)
  if(!page) return undefined
  if(extractCollectionId(pageid) && !page.collection) return undefined
  return {
    page: extractPageId(pageid),
    collection: extractCollectionId(pageid)
  }
}

export const identifyPod = (config: INopress_Template_Config, pageid: string, podid: string) => {
  const page = getPageFromId(config, pageid)
  if(!page) return undefined
  if(extractCollectionId(pageid) && !page.collection) return undefined
  if(extractCollectionId(pageid)) {
    if(!page.collection.pods || page.collection.pods.indexOf(podid) < 0) return undefined
  }
  else {
    if(!page.pods || page.pods.indexOf(podid) < 0) return undefined
  }
  return {
    page: extractPageId(pageid),
    collection: extractCollectionId(pageid),
    pod: podid,
  }
}

const sortByDate = (direction = 1) => (a: any, b: any) => {
  const aDate = a.created_at
  const bDate = b.created_at
  if(aDate > bDate) return direction
  else if(aDate < bDate) return direction * -1
  else return 0
}

const sortByField = (field: string, direction = 1) => (a: any, b: any) => {
  const aValue = a.data[field]
  const bValue = b.data[field]
  if(aValue > bValue) return direction
  else if(aValue < bValue) return direction * -1
  else return 0
}

export const sortPods = (podConfig: INopress_Pod_Config, data: IRecord_Nopress_Pod[]): IRecord_Nopress_Pod[] => {
  if(!podConfig.sorting) return data
  const ret = [].concat(data)
  if(podConfig.sorting.type == 'date') {
    ret.sort(sortByDate(podConfig.sorting.direction == 'asc' ? 1 : -1))
  }
  else if(podConfig.sorting.type == 'field') {
    ret.sort(sortByField(podConfig.sorting.name, podConfig.sorting.direction == 'asc' ? 1 : -1))
  }
  return ret
}

export const mergeStructure = (
  config: INopress_Template_Config,
  data: IData_Nopress_List_Data,
  rootData: IData_Nopress_List_Data,
  orgNode: INode_Org,
  orgUrl: string,
): INopress_Template_Config => {

  let ret = JSON.parse(JSON.stringify(config)) as INopress_Template_Config
  const allPodData = podDataHelper(data)
  const allPageData = pageDataHelper(data)
  const rootPodData = podDataHelper(rootData)
  const rootPageData = pageDataHelper(rootData)
  const structure = structureHelper(ret)

  const processPage = (page: INopress_Page, baseUrl: string): INopress_Page | null => {
    const children = page.pages || []
    const pods = page.pods || []
    
    const pageData = Object.assign({}, allPageData[page.id] || {
      id: 'new',
      meta: {},
      data: {},
    })
    
    const rootFormData = Object.assign({}, rootPageData[page.id] ? rootPageData[page.id].data : {})
    const orgFormData = Object.assign({}, pageData.data)
    
    pageData.data = orgFormData

    if(page.inheritDefaultFields) {
      page.inheritDefaultFields.forEach(name => {
        if(!pageData.data[name]) pageData.data[name] = rootFormData[name]
      })
    }

    if(page.inheritLockedFields) {
      page.inheritLockedFields.forEach(name => {
        pageData.data[name] = rootFormData[name]
      })      
    }

    if(page.valueTags) {
      const orgConfig = orgNode.meta?.appconfig?.nopress || {}
      const orgRenderTags = orgConfig.renderTags || []
      page.valueTags.forEach(valueTag => {
        const matchingCount = valueTag.selector.reduce((all, tag) => {
          return all + orgRenderTags.indexOf(tag) >= 0 ? 1 : 0
        }, 0)
        if(matchingCount >= valueTag.selector.length) {
          page[valueTag.field] = valueTag.value
        }
      })
    }

    page.pageData = pageData
    page.fullUrl = (baseUrl ? [baseUrl, page.url].join('/') : page.url || '').replace(/\/+/g, '/')
    page.orgUrl = orgUrl
    page.podData = {}
    pods.forEach(podName => {
      const podConfig = structure.pods[podName]
      if(!podConfig) throw new Error(`no pod config found :${podName}`)
      const orgPods = allPodData[`${page.id}:${podName}`] || []
      const rootPods = rootPodData[`${page.id}:${podName}`] || []
      const inheritPods = page.inheritLockedPods || []
      const usePods = inheritPods.indexOf(podName) >= 0 ? rootPods : orgPods
      page.podData[podName] = sortPods(podConfig, usePods)
    })

    // we keep a map of the child pages in the parent meta
    // this means we can use the same page system for child pages
    // but the route id will be <page_id>:<collection_id>
    // the meta of the page is what keeps track of what collection_ids belong to this page
    // otherwise we have to use the precense of page data or pod data to detect "there is" a page
    // because pages can have either or both - it's hard to know what to pin the existence of the page on
    // so - we keep track of pages in the parent meta
    if(page.collection) {
      const collectionPods = page.collection.pods || []
      let metaPages: IRecord_Nopress_Page_Core_Metadata_Page[] = [].concat(page.pageData.meta.pages || [])

      if(page.collection.mergeRootCollection) {
        const currentPage = allPageData[page.id]
        const rootPage = rootPageData[page.id]
        const isSamePage = currentPage && rootPage && currentPage.id == rootPage.id ? true : false
        if(!isSamePage) {
          metaPages = metaPages.concat((rootPage.meta.pages || []).map(page => {
            return Object.assign({}, page, {
              inherited: true,
            })
          }))
        }
      }

      if(page.collection.sorting && page.collection.sorting.type == 'date') {
        metaPages.sort(sortByDate(page.collection.sorting.direction == 'asc' ? 1 : -1))
      }

      // map the meta pages into actual child pages
      const collectionPageData = metaPages.map(metaPageConfig => {
        const metaPageId = `${page.id}:${metaPageConfig.id}`
        const collectionPage: INopress_Page = {
          id: metaPageId,
          title: metaPageConfig.title,
          url: metaPageConfig.url,
          fullUrl: [page.fullUrl, metaPageConfig.url].join('/'),
          pageData: allPageData[metaPageId] || rootPageData[metaPageId],
          podData: {},
          layout: page.collection.layout || page.layout,
          pods: page.collection.pods || [],
          forms: page.collection.forms  || [],
        }

        if(metaPageConfig.inherited) collectionPage.inherited = true

        collectionPods.forEach(podName => {
          const podConfig = structure.pods[podName]
          if(!podConfig) throw new Error(`no pod config found :${podName}`)
          collectionPage.podData[podName] = sortPods(podConfig, allPodData[`${metaPageId}:${podName}`] || [])
        })
        return collectionPage
      })

      if(page.collection.sorting && page.collection.sorting.type == 'field') {
        collectionPageData.sort(sortByField(page.collection.sorting.name, page.collection.sorting.direction == 'asc' ? 1 : -1))
      }

      page.pages = collectionPageData
    }
    else {
      page.pages = children
        .map(child => processPage(child, page.fullUrl))
        .filter(p => p)
    }
    
    return page
  }
  ret.content.forEach(section => {      
    section.pages = section.pages
      .map((page) => processPage(page, ''))
      .filter(p => p)
  })
  return ret
}

export const getRootProvider = (
  orgTree: ITree_Org,
  orgId: string,
) => {
  return findParentOrg(orgTree, orgId, org => {
    return getOrgAppConfig(org, 'nopress', 'rootProvider') ? true : false
  }, true)
}

// map an array of form names into the array of actual forms
export const getFormArray = (config: INopress_Template_Config, forms: string[] = []): INopress_Form_Config[] => {
  return forms.map(id => config.forms.find(f => f.id == id))
}

export const getPodArray = (config: INopress_Template_Config, pods: string[] = []): INopress_Pod_Config[] => {
  return pods.map(id => config.pods.find(p => p.id == id))
}

export const getFormSchema = (config: INopress_Template_Config, forms: string[] = []): IForm_Schema => {
  return getFormArray(config, forms).reduce<IForm_Schema>((all, form) => {
    return all.concat(form.schema)
  }, [])
}

export const getOrgUrl = ({
  projectConfig,
  rootProvider,
  orgProfile,
  orgNode,
}: {
  projectConfig: IMeta_ProjectConfig_App_Config_Nopress,
  rootProvider: string,
  orgProfile: IData_Org_With_Profile,
  orgNode: INode_Org,
}) => {
  const {
    mode,
    field,
  } = projectConfig.url
  if(orgNode && rootProvider == orgNode.id) return ''
  if(mode == 'appconfig' && orgNode) {
    const orgConfig = orgNode.meta?.appconfig?.nopress || {}
    return orgConfig.url || ''
  }
  else if(mode == 'profile' && field && orgProfile) {
    return orgProfile.profile[field] || ''
  }
  else {
    return ''
  }
}

export const getPageFullUrl = (page: INopress_Page) => {
  const fullPageUrl = page.fullUrl.replace(/^\//, '')
  const fullOrgUrl = page.orgUrl && page.orgUrl != 'root' ?
    [page.orgUrl, fullPageUrl].join('/') :
    fullPageUrl
  return `./${fullOrgUrl}`
}

export const getFullUrl = (page: INopress_Page, url: string) => {
  const fullPageUrl = url.replace(/^\//, '')
  const fullOrgUrl = page.orgUrl && page.orgUrl != 'root' ?
    [page.orgUrl, fullPageUrl].join('/') :
    fullPageUrl
  return `./${fullOrgUrl}`
}